import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useRecommendation } from "../contexts/RecommendationContext"
import AuthForm from "../components/AuthForm"
import PreferencesForm from "../components/PreferencesForm"

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [showAuthForm, setShowAuthForm] = useState(false)
  const { isAuthenticated, isGuest, createGuestSession } = useAuth()
  const { savePreferences } = useRecommendation()
  const navigate = useNavigate()

  useEffect(() => {
    // If user is already authenticated and not a guest, skip to preferences
    if (isAuthenticated && !isGuest) {
      setCurrentStep(2)
    }
  }, [isAuthenticated, isGuest])

  const handleStartAsGuest = async () => {
    if (!isAuthenticated) {
      const result = await createGuestSession()
      if (result.success) {
        setCurrentStep(2)
      }
    } else {
      setCurrentStep(2)
    }
  }

  const handlePreferencesComplete = async preferences => {
    const result = await savePreferences(preferences)
    if (result.success) {
      navigate("/recommendations")
    }
  }

  const handleAuthComplete = () => {
    setCurrentStep(2)
    setShowAuthForm(false)
  }

  if (currentStep === 2) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Tell Us About Your Preferences</h1>
            <p className="text-lg text-gray-600">This helps us recommend gifts that match your style and taste.</p>
          </div>
          <PreferencesForm onComplete={handlePreferencesComplete} />
        </div>
      </div>
    )
  }

  if (showAuthForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <AuthForm onComplete={handleAuthComplete} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to Givvio</h1>
          <p className="text-xl text-gray-600">Let's get you set up to find amazing gifts</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Guest Option */}
            <div className="text-center p-6 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
              <div className="mb-4">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Start as Guest</h3>
                <p className="text-gray-600 mb-6">
                  Jump right in and start finding gifts. You can create an account later to save your preferences and purchase history.
                </p>
                <button
                  onClick={handleStartAsGuest}
                  className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Continue as Guest
                </button>
              </div>
            </div>

            {/* Account Option */}
            <div className="text-center p-6 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
              <div className="mb-4">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Account</h3>
                <p className="text-gray-600 mb-6">
                  Create an account to save your preferences, track your gift history, and get personalized recommendations.
                </p>
                <button
                  onClick={() => setShowAuthForm(true)}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Sign Up / Sign In
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Don't worry - you can always create an account later and we'll save all your preferences and recommendations.
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Smart Recommendations</h4>
            <p className="text-sm text-gray-600">AI-powered suggestions based on your preferences</p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Privacy First</h4>
            <p className="text-sm text-gray-600">Your data is secure and never shared</p>
          </div>

          <div className="text-center">
            <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Always Free</h4>
            <p className="text-sm text-gray-600">No hidden fees or subscription required</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Onboarding
